import { useRef, useState, type DragEvent, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Eye, EyeOff, FileText, Image, Trash2, Upload } from 'lucide-react';
import {
  ALLOWED_FILE_TYPES,
  fileFolders,
  formatDate,
  MAX_FILE_BYTES,
  type FileFolder,
  type StoredFile,
} from '@vuokramani/shared';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  EmptyState,
  Panel,
  Segmented,
  SelectField,
} from '../../components/ui.tsx';
import { ApiError } from '../../lib/api.ts';
import {
  fileUrl,
  formatBytes,
  isImage,
  useDeleteFile,
  useFiles,
  useUpdateFile,
  useUploadFiles,
} from '../../lib/tenancy.ts';
import s from './tenancy.module.css';

interface Editing {
  propertyId: string;
  editable: boolean;
}

function FileActions({ file, propertyId, editable }: { file: StoredFile } & Editing) {
  const { t } = useTranslation();
  const update = useUpdateFile(propertyId);
  const [confirm, setConfirm] = useState(false);
  const remove = useDeleteFile(propertyId);
  const shareLabel = file.sharedWithTenant ? t('files.unshare') : t('files.share');
  return (
    <>
      {editable && (
        <Button
          variant="ghost"
          icon
          aria-pressed={file.sharedWithTenant}
          aria-label={shareLabel}
          title={shareLabel}
          onClick={() =>
            update.mutate({ id: file.id, body: { sharedWithTenant: !file.sharedWithTenant } })
          }
        >
          {file.sharedWithTenant ? <Eye size={16} className={s.shareOn} /> : <EyeOff size={16} />}
        </Button>
      )}
      <a
        href={fileUrl(file.id, true)}
        className="vm-icon-link"
        aria-label={t('files.download')}
        title={t('files.download')}
      >
        <Download size={16} />
      </a>
      {editable && (
        <>
          <Button
            variant="ghost"
            icon
            aria-label={t('common.delete')}
            title={t('common.delete')}
            onClick={() => setConfirm(true)}
          >
            <Trash2 size={16} />
          </Button>
          <Dialog open={confirm} onClose={() => setConfirm(false)} title={t('files.deleteTitle')}>
            <p>{t('files.deleteText', { name: file.name })}</p>
            <DialogActions>
              <Button variant="ghost" onClick={() => setConfirm(false)}>
                {t('common.cancel')}
              </Button>
              <Button
                variant="primary"
                disabled={remove.isPending}
                onClick={() => remove.mutate(file.id, { onSuccess: () => setConfirm(false) })}
              >
                {t('common.delete')}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </>
  );
}

/**
 * Asiakirjat listana. Nimi avaa tiedoston uuteen välilehteen. showEntry näyttää, minkä
 * huoltokirjan tapahtuman liite tiedosto on (pois päältä tapahtuman omassa listassa).
 */
export function FileList({
  files,
  showEntry = true,
  ...editing
}: { files: StoredFile[]; showEntry?: boolean } & Editing) {
  const { t, i18n } = useTranslation();
  return (
    <ul className={s.list}>
      {files.map((f) => (
        <li key={f.id} className={s.fileRow}>
          <span className={s.fileIcon} aria-hidden="true">
            {isImage(f) ? <Image size={19} /> : <FileText size={19} />}
          </span>
          <span className={s.fileText}>
            <a href={fileUrl(f.id)} target="_blank" rel="noopener" className={s.fileName}>
              {f.name}
            </a>
            <span className={s.fileMeta}>
              {[
                formatBytes(f.sizeBytes, i18n.language),
                formatDate(f.createdAt, i18n.language),
                editing.editable && !f.sharedWithTenant ? t('files.notShared') : null,
                showEntry && f.maintenanceEntry
                  ? t('files.fromMaintenance', { title: f.maintenanceEntry.title })
                  : null,
              ]
                .filter(Boolean)
                .join(', ')}
            </span>
          </span>
          <span className={s.rowActions}>
            <FileActions file={f} {...editing} />
          </span>
        </li>
      ))}
    </ul>
  );
}

/** Kuvat ruudukkona. */
export function PhotoGrid({ files, ...editing }: { files: StoredFile[] } & Editing) {
  return (
    <div className={s.photos}>
      {files.map((f) => (
        <figure key={f.id} className={s.photo}>
          <a href={fileUrl(f.id)} target="_blank" rel="noopener">
            <img src={fileUrl(f.id)} alt={f.name} loading="lazy" />
          </a>
          <figcaption className={s.photoCaption}>
            <span>{f.name.replace(/\.[^.]+$/, '')}</span>
            <span className={s.photoActions}>
              <FileActions file={f} {...editing} />
            </span>
          </figcaption>
        </figure>
      ))}
    </div>
  );
}

/** Kansiot, joissa on tiedostoja, ja näytettävä kansio (toive, jos siinä on tiedostoja). */
function foldersOf(files: StoredFile[], wanted: FileFolder | null) {
  const present = fileFolders.filter((f) => files.some((x) => x.folder === f));
  const current = wanted && present.includes(wanted) ? wanted : (present[0] ?? null);
  return { present, current };
}

/**
 * Tiedostot kansioittain: sopimukset, taloyhtiön asiakirjat, kuitit, kuvat, muut. Kansio
 * kerrallaan; valitsimessa vain kansiot, joissa on tiedostoja. Kuvat ruudukkona, muut listana.
 */
export function FileGroups({
  files,
  folder = null,
  onFolder,
  ...editing
}: {
  files: StoredFile[];
  folder?: FileFolder | null;
  onFolder?: (folder: FileFolder) => void;
} & Editing) {
  const { t } = useTranslation();
  const [own, setOwn] = useState<FileFolder | null>(null);
  const { present, current } = foldersOf(files, onFolder ? folder : own);
  if (!current) return null;
  const inFolder = files.filter((f) => f.folder === current);
  const photos = inFolder.filter(isImage);
  const docs = inFolder.filter((f) => !isImage(f));
  return (
    <div style={{ display: 'grid', gap: '1rem' }}>
      {present.length > 1 ? (
        <Segmented
          label={t('files.folder')}
          value={current}
          options={present.map((f) => ({
            value: f,
            label: t(`folders.${f}`),
            count: files.filter((x) => x.folder === f).length,
          }))}
          onChange={onFolder ?? setOwn}
        />
      ) : (
        <h2 className={s.groupTitle}>
          {t(`folders.${current}`)} <span className={s.groupCount}>{inFolder.length}</span>
        </h2>
      )}
      {photos.length > 0 && <PhotoGrid files={photos} {...editing} />}
      {docs.length > 0 && (
        <Panel>
          <FileList files={docs} {...editing} />
        </Panel>
      )}
    </div>
  );
}

/** Vuokranantajan tiedostot: lataus ja kansioittain ryhmitelty lista. */
export function FilesManager({ propertyId }: { propertyId: string }) {
  const { t } = useTranslation();
  const { data: files } = useFiles(propertyId);
  const [uploading, setUploading] = useState(false);
  const [folder, setFolder] = useState<FileFolder | null>(null);
  const { current } = foldersOf(files ?? [], folder);

  return (
    <>
      <div className={s.toolbar}>
        <p className={s.fileMeta} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Eye size={14} className={s.shareOn} aria-hidden="true" />
          {t('files.legend')}
        </p>
        <Button variant="primary" onClick={() => setUploading(true)}>
          <Upload size={16} aria-hidden="true" />
          {t('files.upload')}
        </Button>
      </div>

      {files && files.length === 0 && (
        <EmptyState
          icon={<FileText size={22} />}
          title={t('files.empty')}
          text={t('files.emptyText')}
        />
      )}
      {files && files.length > 0 && (
        <FileGroups
          files={files}
          folder={current}
          onFolder={setFolder}
          propertyId={propertyId}
          editable
        />
      )}

      {uploading && (
        <UploadDialog
          propertyId={propertyId}
          defaultFolder={current ?? 'other'}
          onUploaded={setFolder}
          onClose={() => setUploading(false)}
        />
      )}
    </>
  );
}

function UploadDialog({
  propertyId,
  defaultFolder,
  onUploaded,
  onClose,
}: {
  propertyId: string;
  defaultFolder: FileFolder;
  /** Ladattu kansioon: näytetään se. */
  onUploaded: (folder: FileFolder) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const upload = useUploadFiles(propertyId);
  const input = useRef<HTMLInputElement>(null);
  const [selected, setSelected] = useState<File[]>([]);
  const [folder, setFolder] = useState<FileFolder>(defaultFolder);
  const [shared, setShared] = useState(defaultFolder !== 'receipt');
  const [drag, setDrag] = useState(false);
  const [localError, setLocalError] = useState<string>();

  function choose(list: FileList | null) {
    const files = Array.from(list ?? []);
    const tooBig = files.find((f) => f.size > MAX_FILE_BYTES);
    setLocalError(tooBig ? t('files.tooLarge', { name: tooBig.name }) : undefined);
    setSelected(files.filter((f) => f.size <= MAX_FILE_BYTES));
    // Kuvat oletuksena Kuvat-kansioon
    if (files.length > 0 && files.every((f) => f.type.startsWith('image/')) && folder === 'other') {
      setFolder('photo');
      setShared(true);
    }
  }

  function onDrop(event: DragEvent) {
    event.preventDefault();
    setDrag(false);
    choose(event.dataTransfer.files);
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    if (selected.length === 0) return;
    upload.mutate(
      { files: selected, folder, shared },
      {
        onSuccess: () => {
          onUploaded(folder);
          onClose();
        },
      },
    );
  }

  const serverError =
    upload.error instanceof ApiError
      ? upload.error.code === 'file_type'
        ? t('files.wrongType', { name: upload.error.message })
        : upload.error.code === 'file_too_large'
          ? t('files.tooLarge', { name: '' })
          : t('errors.generic')
      : upload.isError
        ? t('errors.generic')
        : undefined;

  return (
    <Dialog open onClose={onClose} title={t('files.upload')}>
      <form onSubmit={submit} style={{ display: 'grid', gap: '1.1rem' }}>
        <label
          className={`${s.dropzone} ${drag ? s.dropzoneActive : ''}`}
          onDragOver={(e) => {
            e.preventDefault();
            setDrag(true);
          }}
          onDragLeave={() => setDrag(false)}
          onDrop={onDrop}
        >
          <Upload size={22} aria-hidden="true" />
          <span>{t('files.choose')}</span>
          <span className={s.fileMeta}>{t('files.allowed')}</span>
          <input
            ref={input}
            type="file"
            multiple
            accept={ALLOWED_FILE_TYPES.join(',')}
            className="visually-hidden"
            onChange={(e) => choose(e.target.files)}
          />
        </label>
        {selected.length > 0 && (
          <ul className={s.selected}>
            {selected.map((f) => (
              <li key={f.name}>{f.name}</li>
            ))}
          </ul>
        )}
        <SelectField
          label={t('files.folder')}
          value={folder}
          onChange={(e) => setFolder(e.target.value as FileFolder)}
        >
          {fileFolders.map((f) => (
            <option key={f} value={f}>
              {t(`folders.${f}`)}
            </option>
          ))}
        </SelectField>
        <label className={s.check}>
          <input type="checkbox" checked={shared} onChange={(e) => setShared(e.target.checked)} />
          <span>{t('files.shareOnUpload')}</span>
        </label>
        {(localError || serverError) && <Alert>{localError ?? serverError}</Alert>}
        <DialogActions>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={selected.length === 0 || upload.isPending}
          >
            {upload.isPending
              ? t('files.uploading')
              : t('files.uploadCount', { count: selected.length })}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
